import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';

import { textoDeAjuda } from '@/shared/help/ajudaDosCampos';
import { cn } from '@/shared/lib/cn';

export interface FieldLabelProps {
  htmlFor: string;
  label: string;
  /**
   * Identificador do proprio rotulo, para o controle que nao e campo de formulario
   * (a area editavel do texto rico) se ligar a ele por aria-labelledby.
   */
  id?: string | undefined;
  className?: string | undefined;
  /**
   * Texto do icone de ajuda. Sem ele, o campo procura a propria ajuda no
   * dicionario pela tela e pelo rotulo; `false` desliga a ajuda.
   */
  help?: string | false | undefined;
}

/**
 * Rotulo do campo, com o icone de ajuda (i) quando ha texto para ele.
 *
 * O icone e um botao, e nao um texto que aparece ao passar o mouse: quem usa
 * teclado ou toque tambem precisa chegar a ajuda.
 */
export function FieldLabel({ htmlFor, label, id, className, help }: FieldLabelProps) {
  const rotulo = (
    <label id={id} htmlFor={htmlFor} className={className}>
      {label}
    </label>
  );

  if (help === false) return rotulo;
  if (typeof help === 'string') {
    return <ComAjuda rotulo={rotulo} label={label} texto={help} />;
  }
  return <AjudaDoDicionario rotulo={rotulo} label={label} />;
}

/**
 * Ajuda (i) ao lado de um texto que nao e rotulo de campo, como o titulo de uma
 * coluna (a "Vigencia Atual" da lista de apolices do OnPoint). Mesmo botao e mesmo
 * balao do campo.
 */
export function HelpTip({ label, text }: { label: string; text: string }) {
  return <ComAjuda rotulo={<span>{label}</span>} label={label} texto={text} />;
}

interface AjudaDoDicionarioProps {
  rotulo: ReactElement;
  label: string;
}

// O componente que le a rota so existe dentro do roteador; o campo testado sozinho
// fica com o dicionario geral. Dois componentes, e nao um gancho condicional,
// porque a ordem dos ganchos nao pode depender de onde o campo foi montado.
function AjudaDoDicionario({ rotulo, label }: AjudaDoDicionarioProps) {
  if (useInRouterContext()) return <AjudaDaRota rotulo={rotulo} label={label} />;
  const texto = textoDeAjuda('', label);
  return texto ? <ComAjuda rotulo={rotulo} label={label} texto={texto} /> : rotulo;
}

function AjudaDaRota({ rotulo, label }: AjudaDoDicionarioProps) {
  const { pathname } = useLocation();
  const texto = textoDeAjuda(pathname, label);
  return texto ? <ComAjuda rotulo={rotulo} label={label} texto={texto} /> : rotulo;
}

interface ComAjudaProps {
  rotulo: ReactElement;
  label: string;
  texto: string;
}

function ComAjuda({ rotulo, label, texto }: ComAjudaProps) {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLSpanElement>(null);
  const textoId = `${useId()}-ajuda`;

  // Clique fora fecha a ajuda, como qualquer balao: deixada aberta, ela cobriria o
  // campo de baixo.
  useEffect(() => {
    if (!aberto) return;
    const fora = (event: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(event.target as Node)) {
        setAberto(false);
      }
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  return (
    <span ref={caixa} className="relative flex items-center gap-1.5">
      {rotulo}
      {/* O nome do botao vem do texto escondido, e nao de aria-label: aria-label
          tambem e rotulo, e a busca do campo pelo proprio rotulo passaria a achar
          dois elementos, o campo e o botao. */}
      <button
        type="button"
        aria-expanded={aberto}
        aria-controls={textoId}
        onClick={() => setAberto((atual) => !atual)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setAberto(false);
        }}
        className={cn(
          'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
          'border-ink-300 text-[10px] leading-none font-bold text-ink-500',
          'hover:border-brand-500 hover:text-brand-600',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30',
        )}
      >
        <span aria-hidden="true">i</span>
        <span className="sr-only">Sobre o campo {label}</span>
      </button>
      {aberto ? (
        <span
          id={textoId}
          role="note"
          className={cn(
            'absolute top-full left-0 z-20 mt-1 w-72 max-w-[80vw] rounded-md border',
            'border-line bg-surface p-3 text-xs font-normal text-ink-700 shadow-lg',
          )}
        >
          {texto}
        </span>
      ) : null}
    </span>
  );
}
